import { memo, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  useTrackDeselectedItems,
  useTrackDisabledSelectAll,
  useTrackEnabledIncludeNestedItems,
  useTrackEnabledSelectAll,
} from '@air/analytics';
import { Button } from '@air/primitive-button';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import {
  hydrateSelectedItemsAction,
  resetSelectedItemsAction,
  selectableItemsSelector,
  selectedItemsSelector,
} from '@air/redux-selected-items';
import { ViewTypeName } from '@air/types';
import { cn } from '@air/utils-tailwind';

import { SelectionActionBarComponent } from '~/components/v2/components/shared/selectionBar/SelectionActionBarComponent';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';
import { useAirStore } from '~/utils/ReduxUtils';

export type BoardsAndAssetsSelectionBarV2Props = {
  menuOptions: DropdownMenuOption[];
  prefixBarButtons?: ReactNode;
  suffixBarButtons?: ReactNode;
  selectAllTotalCount?: number | null;
  selectAllEnabled?: boolean;
  stackLeftBarButtons?: boolean;
  omitBoardName?: boolean;
};

export const BoardsAndAssetsSelectionBar = memo(
  ({
    menuOptions,
    prefixBarButtons,
    suffixBarButtons,
    selectAllTotalCount,
    selectAllEnabled,
    stackLeftBarButtons,
    omitBoardName,
  }: BoardsAndAssetsSelectionBarV2Props) => {
    const dispatch = useDispatch();
    const store = useAirStore();
    const { trackDeselectedItems } = useTrackDeselectedItems();
    const { trackDisabledSelectAll } = useTrackDisabledSelectAll();
    const { trackEnabledSelectAll } = useTrackEnabledSelectAll();
    const { trackEnabledIncludeNestedItems } = useTrackEnabledIncludeNestedItems();
    const nestedItemsInitialCountRef = useRef<number | null | undefined>(undefined);
    const selectedItems = useSelector(selectedItemsSelector);
    const selectableItems = useSelector(selectableItemsSelector);
    const boardId = useSelector(centralizedBoardIdSelector);
    const currentViewTypeName = useSelector(currentViewTypeNameSelector);
    const selectAll = useViewsSelectAllStore((state) => state.selectAll);
    const setSelectAll = useViewsSelectAllStore((state) => state.setSelectAll);
    const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);
    const includeNestedItems = useViewsSelectAllStore((state) => state.includeNestedItems);
    const setIncludeNestedItems = useViewsSelectAllStore((state) => state.setIncludeNestedItems);
    const { isSearchActive } = useIsSearchActive();

    const onClearAll = useCallback(() => {
      if (selectAll) {
        trackDisabledSelectAll({ trigger: 'action-bar', totalSelectionCount: selectAllTotalCount ?? 0 });
      } else {
        trackDeselectedItems({
          fileSelectionCount: 0,
          assetSelectionCount: 0,
          boardSelectionCount: 0,
          totalSelectionCount: 0,
          trigger: 'action-bar',
          type: 'none',
        });
      }
      resetSelectAll();
      dispatch(resetSelectedItemsAction());
    }, [dispatch, resetSelectAll, selectAll, selectAllTotalCount, trackDeselectedItems, trackDisabledSelectAll]);

    const canSelectAllInCurrentView = useMemo(() => {
      if (!boardId || !selectAllEnabled) return false;
      return currentViewTypeName === ViewTypeName.gallery || currentViewTypeName === ViewTypeName.table;
    }, [boardId, currentViewTypeName, selectAllEnabled]);

    const hasBoardsInView = useMemo(() => {
      return selectableItems.some((item) => item.type === 'board');
    }, [selectableItems]);

    const toggleSelectAll = useCallback(() => {
      if (selectAll) {
        trackDisabledSelectAll({ trigger: 'action-bar', totalSelectionCount: selectAllTotalCount ?? 0 });
        resetSelectAll();
        dispatch(resetSelectedItemsAction());
        return;
      }

      setSelectAll(boardId);
      // Select only assets and files (not boards)
      const selectableItems = selectableItemsSelector(store.getState());
      const assetsAndFilesOnly = selectableItems.filter((item) => item.type !== 'board');
      dispatch(hydrateSelectedItemsAction({ selectedItems: assetsAndFilesOnly }));
      trackEnabledSelectAll({ trigger: 'action-bar', totalSelectionCount: assetsAndFilesOnly.length });
    }, [
      boardId,
      dispatch,
      resetSelectAll,
      selectAll,
      selectAllTotalCount,
      setSelectAll,
      store,
      trackDisabledSelectAll,
      trackEnabledSelectAll,
    ]);

    const enableIncludeNestedItems = useCallback(() => {
      // Capture count before state change (before cached query can return)
      nestedItemsInitialCountRef.current = selectAllTotalCount;
      setIncludeNestedItems(true);
    }, [selectAllTotalCount, setIncludeNestedItems]);

    // Fire analytics event when includeNestedItems is enabled, waiting for selectAllTotalCount
    // to update from usePrivateSelectAllTotalCount/usePublicSelectAllTotalCount hooks
    useEffect(() => {
      if (!includeNestedItems) {
        // Reset tracking state when nested items is disabled
        nestedItemsInitialCountRef.current = undefined;
        return;
      }

      // initialCount is set in click handler before state changes
      // Fire event if count has changed from that initial value
      const initialCount = nestedItemsInitialCountRef.current;
      if (initialCount !== undefined && selectAllTotalCount && selectAllTotalCount !== initialCount) {
        trackEnabledIncludeNestedItems({ totalSelectionCount: selectAllTotalCount });
        // Set to current to prevent re-firing
        nestedItemsInitialCountRef.current = selectAllTotalCount;
      }
    }, [includeNestedItems, selectAllTotalCount, trackEnabledIncludeNestedItems]);

    if (selectedItems.length === 0) {
      return null;
    }

    return (
      <SelectionActionBarComponent
        menuOptions={menuOptions}
        onClearAll={onClearAll}
        leftBarButtons={
          canSelectAllInCurrentView ? (
            <>
              {!selectAll && !isSearchActive && !!selectAllTotalCount && selectAllTotalCount > 1 && (
                <Button
                  appearance="ghost"
                  className={cn('text-grey-1 hover:text-grey-10 active:text-grey-9', stackLeftBarButtons && 'px-0')}
                  color="grey"
                  data-testid="SELECTION_ACTION_BAR_SELECT_ALL"
                  onClick={toggleSelectAll}
                  size="small"
                >
                  {`Select all ${selectAllTotalCount.toLocaleString()} items`}
                </Button>
              )}
              {selectAll && !includeNestedItems && !isSearchActive && hasBoardsInView && (
                <Button
                  appearance="ghost"
                  className={cn('text-grey-1 hover:text-grey-10 active:text-grey-9', stackLeftBarButtons && 'px-0')}
                  color="grey"
                  onClick={enableIncludeNestedItems}
                  size="small"
                >
                  Include nested items
                </Button>
              )}
            </>
          ) : undefined
        }
        omitBoardName={omitBoardName}
        stackLeftBarButtons={stackLeftBarButtons}
        prefixBarButtons={prefixBarButtons}
        selectAllTotalCount={selectAllTotalCount}
        suffixBarButtons={suffixBarButtons}
      />
    );
  },
);

BoardsAndAssetsSelectionBar.displayName = 'BoardsAndAssetsSelectionBar';
